import React, { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { XCircle, AlertTriangle, TrendingDown } from 'lucide-react';

const REASON_COLORS = ['#EF3340', '#f59e0b', '#8b5cf6', '#3b82f6', '#06b6d4', '#ec4899', '#14b8a6', '#f97316'];

export function RejectionAnalyticsWidget({ period = 'this_month', customStart, customEnd }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({ period });
        if (customStart) params.set('custom_start', customStart);
        if (customEnd) params.set('custom_end', customEnd);
        apiClient.get(`/dashboard/rejection-analytics?${params}`)
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [period, customStart, customEnd]);

    if (loading) return (
        <Card data-testid="rejection-analytics-widget">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" /> Rejection Analytics
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="h-48 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
                </div>
            </CardContent>
        </Card>
    );

    if (!data || data.summary?.total_rejected === 0) return (
        <Card data-testid="rejection-analytics-widget">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" /> Rejection Analytics
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="h-32 flex items-center justify-center text-muted-foreground">
                    No rejected leads in this period
                </div>
            </CardContent>
        </Card>
    );

    const { summary, reasons, agents } = data;

    return (
        <Card data-testid="rejection-analytics-widget">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-red-500" />
                        <CardTitle className="text-base">Rejection Analytics</CardTitle>
                    </div>
                    <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="font-mono" data-testid="rejection-total-badge">
                            {summary.total_rejected} rejected
                        </Badge>
                        <Badge variant="outline" className="font-mono" data-testid="rejection-rate-badge">
                            {summary.rejection_rate}% rate
                        </Badge>
                    </div>
                </div>
                <CardDescription>
                    {summary.total_rejected} of {summary.total_leads} leads rejected
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Reasons Breakdown */}
                    <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                            <AlertTriangle className="h-4 w-4" /> Rejection Reasons
                        </h4>
                        {reasons.length > 0 ? (
                            <div className="flex gap-4">
                                <div className="w-[140px] h-[140px] shrink-0">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={reasons}
                                                dataKey="count"
                                                nameKey="reason"
                                                cx="50%" cy="50%"
                                                innerRadius={30} outerRadius={60}
                                                paddingAngle={2}
                                            >
                                                {reasons.map((_, i) => (
                                                    <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: 'hsl(var(--popover))',
                                                    color: 'hsl(var(--popover-foreground))',
                                                    border: '1px solid hsl(var(--border))',
                                                    borderRadius: '8px',
                                                    fontSize: '12px',
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex-1 space-y-2 overflow-y-auto max-h-[140px]">
                                    {reasons.map((r, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm" data-testid={`rejection-reason-${i}`}>
                                            <div
                                                className="w-3 h-3 rounded-full shrink-0"
                                                style={{ backgroundColor: REASON_COLORS[i % REASON_COLORS.length] }}
                                            />
                                            <span className="truncate flex-1">{r.reason}</span>
                                            <span className="font-mono text-muted-foreground shrink-0">{r.count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">No reason data</p>
                        )}
                    </div>

                    {/* Agent Rejection Rates */}
                    <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
                            <TrendingDown className="h-4 w-4" /> Agent Rejection Rates
                        </h4>
                        {agents.length > 0 ? (
                            <div className="overflow-x-auto max-h-[180px] overflow-y-auto">
                                <Table>
                                    <TableHeader className="sticky top-0 bg-background z-10">
                                        <TableRow>
                                            <TableHead>Agent</TableHead>
                                            <TableHead className="text-right">Rejected</TableHead>
                                            <TableHead className="text-right">Total</TableHead>
                                            <TableHead className="w-[120px]">Rate</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {agents.map((a, i) => (
                                            <TableRow key={a.agent_id || i} data-testid={`rejection-agent-row-${i}`}>
                                                <TableCell className="font-medium text-sm">{a.agent_name}</TableCell>
                                                <TableCell className="text-right font-mono text-red-500">{a.rejected_count}</TableCell>
                                                <TableCell className="text-right font-mono text-muted-foreground">{a.total_leads}</TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Progress
                                                            value={Math.min(a.rejection_rate, 100)}
                                                            className="h-2 flex-1"
                                                        />
                                                        <span className={`font-mono text-xs shrink-0 ${a.rejection_rate > 30 ? 'text-red-500' : a.rejection_rate > 15 ? 'text-amber-500' : 'text-emerald-500'}`}>
                                                            {a.rejection_rate}%
                                                        </span>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">No agent data</p>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default RejectionAnalyticsWidget;
